import { useState, useCallback, useMemo } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { useFileDialog } from '@/hooks/useFileDialog';
import { isProFeature, hasFeature } from '@/lib/license';
import { ProBadge } from '@/components/ProBadge';
import { isDesktop } from '@/lib/platform';
import DesktopHome from '@/components/desktop/DesktopHome';
import {
  CATEGORIES,
  DASHBOARD_TABS,
  localizedLabel,
  localizedToolTitle,
  localizedToolDescription,
} from '@/lib/toolCategories';
import CloudColumn from '@/components/CloudColumn';

const ALL_TOOLS = CATEGORIES.flatMap((category) =>
  category.tools.map((tool) => ({
    ...tool,
    tabId: tool.displayTab || category.id,
    colors: tool.displayColors || category.colors,
  }))
);

function WebDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openFile: openFileDialog } = useFileDialog();
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const openFile = useCallback((file) => {
    if (file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name))) {
      navigate('/viewer', { state: { file } });
    }
  }, [navigate]);

  const handleOpenClick = useCallback(async (mode) => {
    const result = await openFileDialog('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      const path = mode === 'annotate' ? '/viewer?mode=edit'
                 : mode === 'editText' ? '/viewer?mode=editText'
                 : '/viewer';
      // Thread cloudDocId when the user picked from the cloud tab — the
      // Viewer reads it and flips the session to cloud-source.
      navigate(path, {
        state: { file, filePath: result.path, cloudDocId: result.cloudDocId || null },
      });
    }
  }, [openFileDialog, navigate]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    openFile(file);
  }, [openFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const visibleTools = useMemo(
    () => (activeTab === 'all' ? ALL_TOOLS : ALL_TOOLS.filter((t) => t.tabId === activeTab)),
    [activeTab]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-16">
        {/* Hero — Device drop zone + Cloud column (peers, not a banner). */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-12">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => handleOpenClick()}
            className={`lg:col-span-3 border-2 border-dashed rounded-2xl p-6 sm:p-10 text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-brand-blue bg-brand-light scale-[1.01]'
                : 'border-gray-300 hover:border-brand-blue hover:bg-brand-light/30'
            }`}
          >
            <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center ${
              isDragging ? 'bg-brand-blue' : 'bg-brand-light'
            }`}>
              <Upload
                size={24}
                className={isDragging ? 'text-white' : 'text-brand-blue'}
              />
            </div>
            <p className="text-lg font-semibold text-gray-800">
              {t('tools:dashboard.upload_box.title')}, {t('tools:dashboard.upload_box.or')} {t('tools:dashboard.upload_box.browse').toLowerCase()}
            </p>
            <p className="text-sm text-gray-400 mt-1">{t('tools:dashboard.subtitle')}</p>
          </div>
          <div className="lg:col-span-2">
            <CloudColumn />
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {DASHBOARD_TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                  isActive
                    ? 'text-white border-transparent shadow-sm'
                    : 'text-gray-500 border-gray-200 bg-white hover:text-brand-blue hover:border-indigo-200 hover:bg-indigo-50'
                }`}
                style={isActive ? { background: 'linear-gradient(135deg, #4F46E5, #6366F1)' } : undefined}
              >
                {localizedLabel(t, tab)}
              </button>
            );
          })}
        </div>

        {/* Tool grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {visibleTools.map((tool) => (
            <ToolCard key={tool.title} tool={tool} onOpenFile={handleOpenClick} t={t} />
          ))}
        </div>
    </div>
  );
}

export default function Dashboard() {
  if (isDesktop()) return <DesktopHome />;
  return <WebDashboard />;
}

function ToolCard({ tool, onOpenFile, t }) {
  const navigate = useNavigate();
  const { icon: Icon, path, action, feature, pro, colors } = tool;
  const title = localizedToolTitle(t, tool);
  const description = localizedToolDescription(t, tool);
  const isPro = pro || (feature && isProFeature(feature));
  const locked = isPro && !hasFeature(feature);

  const handleClick = () => {
    if (locked) {
      navigate(path || '/');
      return;
    }
    if (action === 'openFile') {
      onOpenFile();
    } else if (action === 'openFileEditText') {
      onOpenFile('editText');
    } else if (action === 'openFileAnnotate') {
      onOpenFile('annotate');
    } else if (action === 'openFileSign') {
      onOpenFile('sign');
    } else if (path) {
      navigate(path);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`group relative flex flex-col items-start text-left p-5 sm:p-6 bg-white rounded-2xl border border-gray-100 transition-all ${
        locked ? 'opacity-60 cursor-pointer' : 'hover:border-indigo-100 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(79,70,229,0.08)]'
      }`}
    >
      {isPro && (
        <div className="absolute top-3 right-3">
          <ProBadge />
        </div>
      )}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: colors.bg, color: colors.fg }}
      >
        <Icon size={24} strokeWidth={1.5} />
      </div>
      <h3 className="text-[0.95rem] font-bold text-gray-900 mb-1.5">{title}</h3>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
    </button>
  );
}
